import React, { createContext, useEffect, useState } from "react";
import "./assets/styles/App.css";
import { observer } from "mobx-react-lite";
import AppRouter from "./routes/AppRouter";
import { Toaster } from "react-hot-toast";
import { Link } from "react-router-dom";
import { useCloud } from "./hooks/useCloud";
import { useStore } from "./hooks/useStore";
import useOrder from "./hooks/useOrder";
import store from "./store/index";
import { updateUserContext } from "./utils/sentryInit";
import { openDB } from "idb";
import { messaging } from "./firebase";
import { onMessage } from "firebase/messaging";
import { usePartnerOrders } from "./hooks/usePartnerOrders";

import {
  transformDigitalOrderItems,
  mapLiveOrderObjectToDashboardObject,
  playRing,
} from "./utils/common";
import { Loading } from "./components/Loading";
import NoInternetAlert from "./components/NoInternetAlert";
import PaymentReminderStripe from "./components/PaymentReminderStripe";
import PaymentWarning from "./components/PaymentWarning";
import usePlan from "./hooks/usePlan";
import DigitalOrderAudio from "./assets/audio/digital-order.mp3";
import PartnerOrderAudio from "./assets/audio/partner-order.mp3";

const DB_NAME = "orderDB";
const IN_PROCESS_ORDERS_STORE_NAME = "InProcessOrders";
const COMPLETE_ORDERS_STORE_NAME = "completedOrders";
const ORDER_HISTORY_STORE_NAME = "orderHistory";
const DB_VERSION = 1;
let pollingID = null;
let newDigitalOrderAlert = null;
let newPartnerOrderAlert = null;
let partnerOrderAlertClearTimeOutCount = 0;
let digitalOrderAlertClearTimeOutCount = 0;

function App() {
  useStore();
  const { liveOrders } = useCloud();
  const { updateOrder, orderExists, saveOrder, fireKOTUpdatedEvent } =
    useOrder();
  const { plan, planString, isPaid, isExpired, isDue, isFree } = usePlan();
  const [digitalOrderReceived, setDigitalOrderReceived] = React.useState(false);
  const [partnerOrderReceived, setPartnerOrderReceived] = React.useState(false);

  // INIT LOCAL DB
  useEffect(() => {
    const initDB = async () => {
      const db = await openDB(DB_NAME, DB_VERSION, {
        upgrade(db) {
          if (!db.objectStoreNames.contains(IN_PROCESS_ORDERS_STORE_NAME)) {
            db.createObjectStore(IN_PROCESS_ORDERS_STORE_NAME, {
              keyPath: "tableVPA",
            });
          }
          if (!db.objectStoreNames.contains(COMPLETE_ORDERS_STORE_NAME)) {
            db.createObjectStore(COMPLETE_ORDERS_STORE_NAME, {
              keyPath: "billNo",
            });
          }
          if (!db.objectStoreNames.contains(ORDER_HISTORY_STORE_NAME)) {
            db.createObjectStore(ORDER_HISTORY_STORE_NAME, {
              keyPath: "billNo",
            });
          }
          if (!db.objectStoreNames.contains("newOrder")) {
            db.createObjectStore("newOrder", {
              keyPath: "order",
            });
          }
        },
      });
      store.setDBInstance(db);
    };

    initDB();
  }, []);

  // Update Sentry user context when restaurantInfo changes
  useEffect(() => {
    if (store.restaurantInfo) {
      updateUserContext();
    }
  }, [store.restaurantInfo]);

  // DIGITAL ORDER NOTIFICATION
  useEffect(() => {
    const unsubscribe = onMessage(messaging, async (payload) => {
      handleNewOrder();
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    clearTimeout(newPartnerOrderAlert);
    partnerOrderAlertClearTimeOutCount = 0;
  }, [store.partnerOrderDetailsOpen]);

  useEffect(() => {
    let isCancelled = false;
    let timeoutID = null;

    const pollLiveOrders = async () => {
      if (!store.isLoggedIn || isCancelled) return;

      const { status, data, billSettlementIds } = await liveOrders();
      if (status === "success") {
        const runningTableVPA = [];
        let digitalOrderCount = 0;
        await Promise.all(
          data.map(mapLiveOrderObjectToDashboardObject).map(async (order) => {
            order.items = order.items.map((item) => ({ ...item }));
            const isExists = await orderExists(order.tableVPA);
            runningTableVPA.push(order.tableVPA);
            if (isExists) updateOrder({ ...order });
            else saveOrder({ ...order });
            window.dispatchEvent(new CustomEvent("liveOrder"));
            if (order.status === "RequestKOT") {
              digitalOrderCount += 1;
              handleNewOrder();
            }
          })
        );

        if (digitalOrderCount === 0) {
          clearTimeout(newDigitalOrderAlert);
          newDigitalOrderAlert = null;
        }

        if (data.length === 0) {
          window.dispatchEvent(new CustomEvent("liveOrder"));
        }

        const db = store.dbInstance;
        if (db) {
          const orders = await db.getAll(IN_PROCESS_ORDERS_STORE_NAME);
          const noExistingOrders = orders.filter(
            (order) => !runningTableVPA.includes(order.tableVPA)
          );
          const settledOrders = orders.filter((order) =>
            [...billSettlementIds, ...store.completedOrderId].includes(
              order.orderID
            )
          );
          const completedVPA = [...settledOrders, ...noExistingOrders].map(
            (order) => order.tableVPA
          );
          completedVPA.forEach((vpa) => {
            db.delete(IN_PROCESS_ORDERS_STORE_NAME, vpa);
            fireKOTUpdatedEvent();
          });
        }
      }

      if (!isCancelled) {
        timeoutID = setTimeout(pollLiveOrders, 5000); // Only schedule next after done
      }
    };

    if (store.isLoggedIn) {
      pollLiveOrders(); // Start the first call
    }

    return () => {
      isCancelled = true;
      clearTimeout(timeoutID);
    };
  }, [store.isLoggedIn]);

  useEffect(() => {
    window.addEventListener(
      "DIGITAL_ORDER_RECEIVED",
      handleNewDigitalOrderReceived
    );
    window.addEventListener(
      "PARTNER_ORDER_RECEIVED",
      handleNewPartnerOrderReceived
    );
    window.addEventListener("PARTNER_ORDER_EMPTY", clearPartnerOrderAlert);
    return () => {
      window.removeEventListener(
        "DIGITAL_ORDER_RECEIVED",
        handleNewDigitalOrderReceived
      );
      window.removeEventListener(
        "PARTNER_ORDER_RECEIVED",
        handleNewPartnerOrderReceived
      );
      window.removeEventListener("PARTNER_ORDER_EMPTY", clearPartnerOrderAlert);
    };
  });

  // useEffect(() => {
  //   const orders = partnerOrders.filter((order) => order.orderStatus === "NEW");

  //   if (
  //     orders.length == 0 &&
  //     !partnerLoading &&
  //     store.partnersConnectionError.length == 0
  //   ) {
  //     clearTimeout(newPartnerOrderAlert);
  //     newPartnerOrderAlert = null;
  //   }

  //   if (
  //     orders.length > 0 &&
  //     !partnerLoading &&
  //     store.partnersConnectionError.length == 0
  //   ) {
  //     handleNewPartnerOrder();
  //   }
  // }, [
  //   partnerOrders,
  //   partnerLoading,
  //   store.partnersConnectionError,
  //   partnerOrders.length,
  // ]);

  const handleNewDigitalOrderReceived = async () => {
    setDigitalOrderReceived(true);
  };

  const handleNewOrder = async () => {
    if (store.mute) return;
    try {
      if (newDigitalOrderAlert) {
        clearTimeout(newDigitalOrderAlert);
      } else {
        setDigitalOrderReceived(true);
        if (!store.mute) await playRing(DigitalOrderAudio);
      }

      newDigitalOrderAlert = setTimeout(async () => {
        setDigitalOrderReceived(true);
        if (!store.mute) await playRing(DigitalOrderAudio);
      }, 120000);
    } catch (e) {
      console.log(e);
    }
  };

  const handleNewPartnerOrderReceived = async () => {
    if (store.partnerOrderDetailsOpen) return;
    try {
      if (newPartnerOrderAlert && partnerOrderAlertClearTimeOutCount < 15) {
        clearTimeout(newPartnerOrderAlert);
        partnerOrderAlertClearTimeOutCount++;
      } else {
        setPartnerOrderReceived(true);
        if (!store.mute) await playRing(PartnerOrderAudio);
        partnerOrderAlertClearTimeOutCount = 0;
      }
      newPartnerOrderAlert = setTimeout(async () => {
        setPartnerOrderReceived(true);
        if (!store.mute) await playRing(PartnerOrderAudio);
        partnerOrderAlertClearTimeOutCount = 0;
      }, 30000);
    } catch (e) {
      console.log(e);
    }
  };

  const clearPartnerOrderAlert = () => {
    setPartnerOrderReceived(false);
    clearTimeout(newPartnerOrderAlert);
    newPartnerOrderAlert = null;
    partnerOrderAlertClearTimeOutCount = 0;
  };

  if (store.appBooting) {
    return (
      <div className="w-screen h-screen flex justify-center items-center gap-2 flex-col">
        <Loading type="dots" size="lg" color="success" />{" "}
        <p className="text-xl font-bold">QRapid Booting up</p>
      </div>
    );
  }

  return (
    <div className="w-screen h-screen">
      
      {/* PAYMENT WARNING MODAL */}
      <PaymentWarning />
      {/* INTERNET CONNECTIVITY ALERT */}
      <NoInternetAlert />
      {/* DIGITAL ORDER */}
      {digitalOrderReceived && (
        <div
          class="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50"
          style={{ zIndex: 501 }}
          onClick={() => setDigitalOrderReceived(false)}
        >
          <div class="bg-white rounded-lg shadow-lg p-6 w-96 text-center relative">
            <div class="absolute top-[10px] left-1/2 transform -translate-x-1/2 text-3xl text-green-500">
              <i class="fas fa-exclamation-circle"></i>
            </div>
            <div class="text-xl font-semibold flashing text-gray-800 mt-6">
              Digital Order Received
            </div>
          </div>
        </div>
      )}
      {partnerOrderReceived && (
        // <Link to="/order-parcel" onClick={() => setPartnerOrderReceived(false)}>
        <div
          class="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50"
          style={{ zIndex: 501 }}
          onClick={() => {
            setPartnerOrderReceived(false);
          }}
        >
          <div class="bg-white rounded-lg shadow-lg p-6 w-96 text-center relative">
            <div class="absolute top-[10px] left-1/2 transform -translate-x-1/2 text-3xl text-green-500">
              <i class="fas fa-exclamation-circle"></i>
            </div>
            <div class="text-xl font-semibold flashing text-gray-800 mt-6">
              Partner Order Received
            </div>
          </div>
        </div>
        // </Link>
      )}
      <Toaster position="top-right" toastOptions={{ duration: 1000 }} />
      {/* HEADER */}
      {/* SIDEBAR */}
      <AppRouter />
    </div>
  );
}

export default observer(App);
